import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Wrench,
  Users,
  Truck,
  Package,
  FileText,
  UserCog,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Home,
  TruckIcon,
  DollarSign,
  Settings,
  User,
  Key,
  ClipboardList,
  FileCheck,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = () => {
  const { user, logout, hasPermission, updateUser } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);

  const handleEditProfile = () => {
    setProfileData({
      name: user?.name || "",
      username: user?.username || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setProfileDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    // Validate passwords if changing
    if (profileData.newPassword) {
      if (profileData.newPassword !== profileData.confirmPassword) {
        toast.error("New passwords do not match");
        return;
      }
      if (profileData.newPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      const updateData = {
        name: profileData.name,
        username: profileData.username,
      };
      
      if (profileData.newPassword) {
        updateData.password = profileData.newPassword;
      }

      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        toast.success("Profile updated successfully");
        // Update local user data
        if (updateUser) {
          updateUser({ ...user, name: profileData.name, username: profileData.username });
        }
        setProfileDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: Home, permission: "dashboard" },
    { path: "/jobs", label: "Jobs", icon: Wrench, permission: "jobs" },
    { path: "/deliveries", label: "Deliveries", icon: TruckIcon, permission: "deliveries" },
    { path: "/pdrs", label: "PDR Forms", icon: ClipboardList, permission: "pdrs" },
    { path: "/job-orders", label: "Job Orders", icon: FileCheck, permission: "job_orders" },
    { path: "/customers", label: "Customers", icon: Users, permission: "customers" },
    { path: "/excavators", label: "Excavators", icon: Truck, permission: "excavators" },
    { path: "/parts", label: "Inventory", icon: Package, permission: "parts" },
    { path: "/invoices", label: "Invoices", icon: FileText, permission: "invoices" },
    { path: "/financials", label: "Financials", icon: DollarSign, permission: "financials" },
    { path: "/settings", label: "Settings", icon: Settings, permission: "settings" },
    { path: "/users", label: "Users", icon: UserCog, permission: "users" },
  ];

  const filteredNavItems = navItems.filter((item) => hasPermission(item.permission));

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-slate-800 text-white p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-700"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <img src="/logo.png" alt="Poh Li Heng" className="h-8" />
        </div>
        <div className="text-sm">
          <span className="text-slate-400">{user?.name}</span>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-slate-800 text-white
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            pt-16 lg:pt-0
          `}
        >
          <div className="p-6 border-b border-slate-700 hidden lg:block">
            <img src="/logo.png" alt="Poh Li Heng" className="h-10" />
            <p className="text-xs text-slate-400 mt-2 uppercase tracking-wider">
              Fleet Management
            </p>
          </div>

          <nav className="p-4 space-y-1 mt-4 lg:mt-0 pb-48 overflow-y-auto max-h-[calc(100vh-100px)] lg:max-h-[calc(100vh-200px)]">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-sm transition-all
                  ${
                    isActive(item.path)
                      ? "bg-blue-500 text-black font-semibold"
                      : "text-slate-300 hover:bg-slate-700 hover:text-white"
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-body">{item.label}</span>
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-800">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
            </div>
            <Button
              variant="ghost"
              onClick={handleEditProfile}
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700 mb-1"
              data-testid="edit-profile-btn"
            >
              <User className="w-5 h-5 mr-3" />
              Edit Profile
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Profile Edit Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase flex items-center gap-2">
                <User className="w-5 h-5" />
                Edit Profile
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Name</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="Your name"
                  className="rounded-sm"
                  data-testid="profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Username</Label>
                <Input
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  placeholder="Username"
                  className="rounded-sm"
                  data-testid="profile-username"
                />
              </div>
              
              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Change Password (optional)
                </p>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">New Password</Label>
                    <Input
                      type="password"
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData({ ...profileData, newPassword: e.target.value })}
                      placeholder="Enter new password"
                      className="rounded-sm"
                      data-testid="profile-new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      className="rounded-sm"
                      data-testid="profile-confirm-password"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setProfileDialogOpen(false)}
                  className="flex-1 rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex-1 bg-slate-800 hover:bg-slate-900 text-white font-bold uppercase tracking-wider rounded-sm"
                  data-testid="save-profile-btn"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen lg:min-h-[calc(100vh)]">
          <div className="p-4 lg:p-6 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
